import m from "mithril";
//import ExpiryStorage from "../js/expiryStorage.js";
import { getUserData, getMithril } from "../utils.js";
import { getTerm, nomenclatureStream } from "../js/nomenclature.js";

// Function to generate random colors with sufficient contrast
function generateRandomColor() {
    const colors = [
        'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-yellow-500', 'bg-lime-500',
        'bg-green-500', 'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500', 'bg-sky-500',
        'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500',
        'bg-pink-500', 'bg-rose-500'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
}

var Layout = {
    oninit: function (vnode) {
        this.isSidebarOpen = true;
        // Title handled in view for reactivity
        // Initialize theme based on saved preference or system preference
        const savedTheme = localStorage.getItem('theme');
        const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        this.isDarkMode = savedTheme ? savedTheme === 'dark' : systemPrefersDark;

        // Apply theme on init
        if (this.isDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }

        // Generate random color for user avatar
        this.userAvatarColor = generateRandomColor();

        // Dropdown state
        this.isDropdownOpen = false;
        this.isDevToolsOpen = false; // For dev tools dropdown
        this.isPengaturanOpen = false; // For pengaturan dropdown
        this.isKoordinatorOpen = false; // For koordinator dropdown
        this.isPinjamanOpen = false; // For pinjaman dropdown
        this.isSimpananOpen = false; // For simpanan dropdown
        this.isNasabahOpen = false; // For nasabah dropdown
        this.isAkuntansiOpen = false; // For akuntansi dropdown
        this.isAktivitasLainOpen = false; // For aktivitas lain dropdown
        this.isLaporanOpen = false; // For laporan dropdown
        this.isKinerjaOpen = false; // For kinerja dropdown

        // User data state
        this.userData = null;
        this.isLoading = true;
        this.missedJasaCount = 0;

        // Subscribe to nomenclature changes for reactive updates
        this.nomenclatureUnsubscribe = nomenclatureStream.map((newNomenclature) => {
            //   console.log('Layout: Nomenclature stream updated, triggering redraw');
            //   console.log('New navigation.customers value:', newNomenclature?.navigation?.customers);
            m.redraw();
        });

        // Load user data asynchronously
        this.loadUserData().then(() => {
            // After user data is loaded and we have subunitId, load missed jasa count
            if (this.userData && ['manajer', 'kasir', 'superuser', 'admin'].includes(this.userData.role)) {
                this.loadMissedJasaCount();
            }
        });
    },

    loadMissedJasaCount: async function () {
        try {
            const data = await getMithril('/api/savings-interest/missed-count', { 
                component: this,
                showLoading: false // Don't show global loading for background notification check
            });
            if (data.success) {
                this.missedJasaCount = data.count || 0;
                m.redraw();
            }
        } catch (error) {
            console.error('Error loading missed jasa count:', error);
        }
    },

    onremove: function () {
        // Unsubscribe from nomenclature stream
        if (this.nomenclatureUnsubscribe) {
            this.nomenclatureUnsubscribe();
        }
    },

    loadUserData: async function () {
        try {
            //  console.log('🔥 LAYOUT DEBUG: Loading user data asynchronously...');

            // Force getUserData to make a fresh API call
            const userData = await getUserData(true);

            //    console.log('🔥 LAYOUT DEBUG: User data loaded:', userData);
            //   console.log('🔥 LAYOUT DEBUG: userData.role:', userData.role);

            // Validate we got valid user data
            if (!userData || userData.authenticated === false) {
                console.error('🚨 LAYOUT DEBUG: No valid user data - redirecting to login');
                this.isLoading = false;
                window.location.href = '/login';
                return;
            }

            this.userData = userData;
            this.isLoading = false;

            //    console.log('🔍 LAYOUT DEBUG: User data loaded successfully');
            //  console.log('🔍 LAYOUT DEBUG: Final role:', userData.role);

            // Trigger redraw with new data
            m.redraw();

        } catch (error) {
            console.error('🚨 LAYOUT DEBUG: Error loading user data:', error);
            this.isLoading = false;
            window.location.href = '/login';
        }
    },

    toggleSidebar: function () {
        this.isSidebarOpen = !this.isSidebarOpen;
    },

    toggleTheme: function () {
        this.isDarkMode = !this.isDarkMode;
        if (this.isDarkMode) {
            document.documentElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    },

    // Toggle dropdown menu
    toggleDropdown: function () {
        this.isDropdownOpen = !this.isDropdownOpen;
    },

    // Toggle dev tools dropdown
    toggleDevTools: function () {
        this.isDevToolsOpen = !this.isDevToolsOpen;
    },

    // Close dropdown when clicking outside
    onclickOutside: function () {
        this.isDropdownOpen = false;
        this.isDevToolsOpen = false;
        this.isNasabahOpen = false;
        this.isKoordinatorOpen = false;
    },

    // Navigate to profile page
    goToProfile: function () {
        // Close dropdown
        this.isDropdownOpen = false;
        // Navigate to profile page
        m.route.set('/profile');
    },

    // Handle logout
    handleLogout: async function () {
        try {
            // Close all dropdowns and reset state
            this.isDropdownOpen = false;
            this.isDevToolsOpen = false;
            this.isPengaturanOpen = false;
            this.isKoordinatorOpen = false;
            this.isPinjamanOpen = false;
            this.isSimpananOpen = false;
            this.isNasabahOpen = false;
            this.isAkuntansiOpen = false;
            this.isLaporanOpen = false;
        this.isKinerjaOpen = false; // For kinerja dropdown

            // Clear all user data from localStorage
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('jwt') ||
                    key.startsWith('username') ||
                    key.startsWith('role') ||
                    key.startsWith('fullName') ||
                    key.startsWith('email') ||
                    key.startsWith('phone') ||
                    key.startsWith('subunit_')) {
                    localStorage.removeItem(key);
                }
            });

            // Clear all user data from sessionStorage
            Object.keys(sessionStorage).forEach(key => {
                if (key.startsWith('jwt') ||
                    key.startsWith('username') ||
                    key.startsWith('role') ||
                    key.startsWith('fullName') ||
                    key.startsWith('email') ||
                    key.startsWith('phone') ||
                    key.startsWith('subunit_')) {
                    sessionStorage.removeItem(key);
                }
            });

            // Clear ExpiryStorage items
            if (typeof ExpiryStorage !== 'undefined') {
                ExpiryStorage.remove('jwt');
                ExpiryStorage.remove('username');
                ExpiryStorage.remove('role');
                ExpiryStorage.remove('user_id');
                ExpiryStorage.remove('fullName');
                ExpiryStorage.remove('email');
                ExpiryStorage.remove('phone');
                ExpiryStorage.remove('subunit_id');
                ExpiryStorage.remove('subunit_name');
                ExpiryStorage.remove('subunit_assigned');
            }

            // Call server-side logout endpoint to clear session
            try {
                await fetch('/api/secure-auth/logout', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    credentials: 'include'
                });
                //    console.log('🔐 Server-side logout successful');
            } catch (logoutError) {
                console.warn('🔐 Server logout failed, but continuing with client-side cleanup:', logoutError);
            }

            // Force a page reload to ensure clean state
            window.location.href = '/login';

        } catch (error) {
            console.error('Logout error:', error);
            // Even if there's an error, try to redirect
            window.location.href = '/login';
        }
    },

    view: function (vnode) {
        // Check if we're on the unprotected landing page (root route)
        const isUnprotectedRoute = m.route.get() === "/";

        // Check if we're on the login page
        const isLoginPage = m.route.get() === "/login";

        // Show loading state while user data is being loaded
        if (this.isLoading || !this.userData) {
            //  console.log('🔥 LAYOUT DEBUG: Still loading user data...');
            return m("div", { class: "flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900" }, [
                m("div", { class: "text-center" }, [
                    m("div", { class: "animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500 mx-auto" }),
                    m("p", { class: "mt-4 text-gray-600 dark:text-gray-400" }, "Loading...")
                ])
            ]);
        }

        const userData = this.userData;
        const jwt = userData.jwt;
        let isAuthenticated = !!jwt;

        // If no JWT but we're authenticated, try to validate session
        if (!isAuthenticated && !isUnprotectedRoute && !isLoginPage) {
            isAuthenticated = true;
        }

        //   console.log('🔥 LAYOUT DEBUG: Layout component is rendering...');

        // Get user properties - EXPLICITLY ensure we get the role correctly
        const username = userData.username;
        let role = userData.role;
        const fullName = userData.fullName;
        const email = userData.email;

        // Read mfa_enabled - Prioritize fresh API data if available
        // Fallback to storage only for instant reactivity during the current session
        const mfa_l = localStorage.getItem('mfa_enabled');
        const mfa_s = sessionStorage.getItem('mfa_enabled');
        const storageMfa = mfa_l === 'true' || mfa_s === 'true';

        // Final status: prefer API data (source of truth)
        const mfaEnabled = (userData && userData.mfa_enabled !== undefined && userData.mfa_enabled !== null)
            ? userData.mfa_enabled
            : storageMfa;

        // DEBUG: Check why badge isn't showing
        console.log('Layout: rendering user data', {
            mfaEnabled,
            apiMfa: userData?.mfa_enabled,
            mfa_l,
            mfa_s,
            type: typeof mfaEnabled,
            showBadge: !mfaEnabled
        });

        // CRITICAL FIX: If role is missing or invalid, try to get it from the backend directly
        if (!role || role === 'user' || role === 'User') {
            console.warn('🚨 LAYOUT DEBUG: Invalid role detected, role:', role);
            console.warn('🚨 LAYOUT DEBUG: This suggests a data flow issue');

            // Log the entire userData to debug
            console.log('🚨 LAYOUT DEBUG: Complete userData object:', userData);

            // Don't proceed with invalid role - this will cause navigation issues
            if (!isUnprotectedRoute && !isLoginPage) {
                // Clear cache and try once more
                //        console.log('🚨 LAYOUT DEBUG: Retrying with fresh cache clear');
                localStorage.removeItem('role');
                sessionStorage.removeItem('role');

                // Reload user data
                this.loadUserData();

                return m("div", { class: "flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900" }, [
                    m("div", { class: "text-center" }, [
                        m("div", { class: "animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500 mx-auto" }),
                        m("p", { class: "mt-4 text-gray-600 dark:text-gray-400" }, "Retrying...")
                    ])
                ]);
            }
        }

        // console.log('🔍 LAYOUT DEBUG: Final role detection:', {
        //     originalUserDataRole: userData.role,
        //     finalRole: role,
        //     username: username,
        //     fullName: fullName,
        //     email: email,
        //     authenticated: isAuthenticated,
        //     rawUserData: userData
        // });

        // Check user roles
        const isSuperuser = role === 'superuser';
        const isManager = role === 'manajer';
        const isAdmin = role === 'admin';
        const isKoordinator = role === 'koordinator';
        const isKasir = role === 'kasir';
        const isAkuntansi = role === 'akuntansi';
        const isBagianKredit = role === 'bagian_kredit';
        
        // Comprehensive management role check
        const isManagement = isSuperuser || isManager || isAdmin;
        
        // Granular grouping for operational subunit-level access
        const isSubunitManager = isManager || isAdmin;
        const isSubunitStaff = isManager || isAdmin || isAkuntansi || isKasir;

        // console.log('🔍 LAYOUT DEBUG: Role analysis:', {
        //     role: role,
        //     isSuperuser: isSuperuser,
        //     isManager: isManager,
        //     isKoordinator: isKoordinator,
        //     shouldShowBadanUsaha: isSuperuser && !isKoordinator
        // });

        // Get first letter of full name or username for avatar
        const avatarLetter = (fullName || username || 'U').charAt(0).toUpperCase();

        return m("div", { class: "flex h-screen bg-gray-100 dark:bg-gray-900 print:h-auto print:bg-white" }, [
            // Sidebar - only show if user is authenticated
            (isUnprotectedRoute && !isAuthenticated) ? null : m("aside", {
                class: (this.isSidebarOpen ? "w-64" : "w-16") + " bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300 ease-in-out print:hidden"
            }, [
                // Sidebar header
                m("div", { class: "border-b border-gray-200 dark:border-gray-700 flex items-center justify-between h-20 px-4 min-h-[5rem]" }, [
                    this.isSidebarOpen ?
                        m("img", { src: "/img/headertop.png", alt: "VITTA", class: "h-[72px] w-auto object-contain max-w-[200px]" }) :
                        m("div", { class: "h-20 flex items-center" }, [
                            m("h1", { class: "text-2xl font-bold text-green-600 dark:text-green-300 " }, "V")
                        ]),
                    m("button", {
                        class: "p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none transition-colors shrink-0",
                        onclick: () => this.toggleSidebar()
                    }, [
                        m("i", { class: this.isSidebarOpen ? "ri-menu-fold-line text-xl" : "ri-menu-unfold-line text-xl" })
                    ])
                ]),

                // Sidebar menu
                m("div", { class: "flex-1 py-4" }, [
                    m("ul", { class: "space-y-1" }, [
                        !isKasir && !isAkuntansi && !isBagianKredit ? m("li", [
                            m("a", {
                                href: "/dashboard",
                                class: "flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors " +
                                    (m.route.get() === "/dashboard" ? "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-orange-500 dark:border-orange-400 text-gray-900 dark:text-white" : ""),
                                oncreate: m.route.link
                            }, [
                                m("i", { class: "ri-dashboard-line mr-3 text-lg" }),
                                this.isSidebarOpen && m("span", { class: "font-medium" }, "Dashboard")
                            ])
                        ]) : null,
                        // Akuntansi specific dashboard link
                        isAkuntansi ? m("li", [
                            m("a", {
                                href: "/akuntansi-dashboard",
                                class: "flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors " +
                                    (m.route.get() === "/akuntansi-dashboard" ? "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-orange-500 dark:border-orange-400 text-gray-900 dark:text-white" : ""),
                                oncreate: m.route.link
                            }, [
                                m("i", { class: "ri-dashboard-line mr-3 text-lg" }),
                                this.isSidebarOpen && m("span", { class: "font-medium" }, "Dashboard Akuntansi")
                            ])
                        ]) : null,
                        // Kasir specific dashboard link
                        isKasir ? m("li", [
                            m("a", {
                                href: "/kasir-dashboard",
                                class: "flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors " +
                                    (m.route.get() === "/kasir-dashboard" ? "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-orange-500 dark:border-orange-400 text-gray-900 dark:text-white" : ""),
                                oncreate: m.route.link
                            }, [
                                m("i", { class: "ri-home-4-line mr-3 text-lg" }),
                                this.isSidebarOpen && m("span", { class: "font-medium" }, "Dashboard Kasir")
                            ])
                        ]) : null,
                        // Koordinator specific navigation
                        isKoordinator ? [
                            // Nasabah (read-only)
                            m("li", [
                                m("a", {
                                    href: "/coord-customers",
                                    class: "flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors " +
                                        (m.route.get() === "/coord-customers" ? "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-orange-500 dark:border-orange-400 text-gray-900 dark:text-white" : ""),
                                    oncreate: m.route.link
                                }, [
                                    m("i", { class: "ri-contacts-line mr-3 text-lg" }),
                                    this.isSidebarOpen && m("span", { class: "font-medium" }, getTerm('navigation.customers', 'Nasabah'))
                                ])
                            ]),
                            m("li", [
                                m("a", {
                                    href: "/savings-deposits",
                                    class: "flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors " +
                                        (m.route.get() === "/savings-deposits" ? "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-orange-500 dark:border-orange-400 text-gray-900 dark:text-white" : ""),
                                    oncreate: m.route.link
                                }, [
                                    m("i", { class: "ri-bank-line mr-3 text-lg" }),
                                    this.isSidebarOpen && m("span", { class: "font-medium" }, getTerm('navigation.savings', 'Simpanan'))
                                ])
                            ]),
                            m("li", [
                                m("a", {
                                    href: "/loan-installments",
                                    class: "flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors " +
                                        (m.route.get() === "/loan-installments" ? "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-orange-500 dark:border-orange-400 text-gray-900 dark:text-white" : ""),
                                    oncreate: m.route.link
                                }, [
                                    m("i", { class: "ri-money-dollar-circle-line mr-3 text-lg" }),
                                    this.isSidebarOpen && m("span", { class: "font-medium" }, getTerm('navigation.loanInstallments', 'Angsuran'))
                                ])
                            ])
                        ] : null,
                        // Show Nasabah link with sublinks to subunit-level management roles
                        isSubunitManager ? [
                            m("li", [
                                m("a", {
                                    href: "#",
                                    class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        // Toggle nasabah menu
                                        this.isNasabahOpen = !this.isNasabahOpen;
                                    }
                                }, [
                                    m("div", { class: "flex items-center" }, [
                                        m("i", { class: "ri-contacts-line mr-3 text-lg" }),
                                        this.isSidebarOpen && m("span", { class: "font-medium truncate" }, getTerm('navigation.customers', 'Nasabah'))
                                    ]),
                                    this.isSidebarOpen && m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isNasabahOpen ? "transform rotate-180" : "") })
                                ]),
                                // Collapsible Menu for Nasabah
                                this.isNasabahOpen && this.isSidebarOpen && m("ul", { class: "mt-1 space-y-1 pl-4 max-w-full" }, [
                                    m("li", [
                                        m("a", {
                                            href: "/customers",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                                (m.route.get() === "/customers" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-group-line mr-3" }),
                                            getTerm('navigation.customerRegistration', 'Pendaftaran')
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/kelompok",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                                (m.route.get() === "/kelompok" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-team-line mr-3" }),
                                            getTerm('navigation.groups', 'Kelompok')
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/new-account",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/new-account" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-bank-card-line mr-3" }),
                                            "Rekening Baru"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/account-list",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/account-list" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-bank-line mr-3" }),
                                            "Daftar Rekening"
                                        ])
                                    ])
                                ])
                            ])
                        ] : null,
                        // Show Pinjaman link with sublinks to subunit-level management roles
                        (isSubunitManager || isBagianKredit) ? [
                            m("li", [
                                m("a", {
                                    href: "#",
                                    class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        // Toggle pinjaman menu
                                        this.isPinjamanOpen = !this.isPinjamanOpen;
                                    }
                                }, [
                                    m("div", { class: "flex items-center" }, [
                                        m("i", { class: "ri-money-dollar-circle-line mr-3 text-lg" }),
                                        this.isSidebarOpen && m("span", { class: "font-medium truncate" }, getTerm('navigation.loans', 'Pinjaman'))
                                    ]),
                                    this.isSidebarOpen && m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isPinjamanOpen ? "transform rotate-180" : "") })
                                ]),
                                // Collapsible Menu for Pinjaman
                                this.isPinjamanOpen && this.isSidebarOpen && m("ul", { class: "mt-1 space-y-1 pl-4 max-w-full" }, [
                                    (isSubunitManager || isBagianKredit) ? m("li", [
                                        m("a", {
                                            href: "/loan-list",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                                (m.route.get() === "/loan-list" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-list-check-2 mr-3" }),
                                            "Daftar Pinjaman"
                                        ])
                                    ]) : null,
                                    isSubunitManager ? m("li", [
                                        m("a", {
                                            href: "/loan-submissions",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                                (m.route.get() === "/loan-submissions" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-file-add-line mr-3" }),
                                            "Pengajuan"
                                        ])
                                    ]) : null,
                                    (isSubunitManager || isBagianKredit) ? m("li", [
                                        m("a", {
                                            href: "/loan-approvals",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/loan-approvals" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-checkbox-circle-line mr-3" }),
                                            "Persetujuan"
                                        ])
                                    ]) : null,
                                    isSubunitManager ? m("li", [
                                        m("a", {
                                            href: "/loan-disbursements",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/loan-disbursements" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-hand-coin-line mr-3" }),
                                            "Pencairan"
                                        ])
                                    ]) : null,
                                    isSubunitManager ? m("li", [
                                        m("a", {
                                            href: "/loan-installments",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/loan-installments" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-list-check mr-3" }),
                                            "Angsuran"
                                        ])
                                    ]) : null,
                                    isSubunitManager ? m("li", [
                                        m("a", {
                                            href: "/loan-settlements",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/loan-settlements" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-flag-2-line mr-3" }),
                                            "Penyelesaian"
                                        ])
                                    ]) : null,
                                    (isManager || isBagianKredit) ? m("li", [
                                        m("a", {
                                            href: "/loan-monitoring",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/loan-monitoring" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-search-eye-line mr-3" }),
                                            "Monitoring"
                                        ])
                                    ]) : null,
                                    isSubunitManager ? m("li", [
                                        m("a", {
                                            href: "/loan-mining",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/loan-mining" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-database-line mr-3" }),
                                            "Proses Mining"
                                        ])
                                    ]) : null,
                                    isSubunitManager ? m("li", [
                                        m("a", {
                                            href: "/loan-prediction",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/loan-prediction" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-brain-line mr-3" }),
                                            "Prediksi Kelancaran"
                                        ])
                                    ]) : null
                                ])
                            ])
                        ] : null,
                        // Show Simpanan link with sublinks to subunit-level management roles
                        isSubunitManager ? [
                            m("li", [
                                m("a", {
                                    href: "#",
                                    class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        // Toggle simpanan menu
                                        this.isSimpananOpen = !this.isSimpananOpen;
                                    }
                                }, [
                                    m("div", { class: "flex items-center" }, [
                                        m("i", { class: "ri-bank-line mr-3 text-lg" }),
                                        this.isSidebarOpen && m("span", { class: "font-medium truncate" }, getTerm('navigation.savings', 'Simpanan')),
                                        (this.isSidebarOpen && this.missedJasaCount > 0) && m("span", {
                                            class: "ml-2 flex h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-800"
                                        })
                                    ]),
                                    this.isSidebarOpen && m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isSimpananOpen ? "transform rotate-180" : "") })
                                ]),
                                // Collapsible Menu for Simpanan
                                this.isSimpananOpen && this.isSidebarOpen && m("ul", { class: "mt-1 space-y-1 pl-4 max-w-full" }, [
                                    m("li", [
                                        m("a", {
                                            href: "/savings-list",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/savings-list" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-file-list-3-line mr-3" }),
                                            "Daftar Simpanan"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/savings-deposits",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/savings-deposits" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-add-circle-line mr-3" }),
                                            "Setoran"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/savings-withdrawals",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/savings-withdrawals" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-subtract-line mr-3" }),
                                            "Penarikan"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/savings-interest",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/savings-interest" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("div", { class: "flex items-center justify-between w-full" }, [
                                                m("div", { class: "flex items-center" }, [
                                                    m("i", { class: "ri-hand-coin-line mr-3" }),
                                                    "Pembayaran Jasa"
                                                ]),
                                                this.missedJasaCount > 0 && m("span", {
                                                    class: "flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full"
                                                }, this.missedJasaCount)
                                            ])
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/savings-performance",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/savings-performance" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-line-chart-line mr-3" }),
                                            "Kinerja Simpanan"
                                        ])
                                    ])
                                ])
                            ])
                        ] : null,


                        // Show Pinjaman menu to Kasir only (Angsuran, Penyelesaian)
                        // Show Anggota/Nasabah menu to Kasir
                        isKasir ? [
                            m("li", [
                                m("a", {
                                    href: "#",
                                    class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        // Toggle nasabah menu
                                        this.isNasabahOpen = !this.isNasabahOpen;
                                    }
                                }, [
                                    m("div", { class: "flex items-center" }, [
                                        m("i", { class: "ri-contacts-line mr-3 text-lg" }),
                                        this.isSidebarOpen && m("span", { class: "font-medium truncate" }, "Anggota")
                                    ]),
                                    this.isSidebarOpen && m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isNasabahOpen ? "transform rotate-180" : "") })
                                ]),
                                // Collapsible Menu for Nasabah
                                this.isNasabahOpen && this.isSidebarOpen && m("ul", { class: "mt-1 space-y-1 pl-4 max-w-full" }, [
                                    m("li", [
                                        m("a", {
                                            href: "/customers",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                                (m.route.get() === "/customers" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-group-line mr-3" }),
                                            "Pendaftaran"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/kelompok",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                                (m.route.get() === "/kelompok" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-team-line mr-3" }),
                                            "Kelompok"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/new-account",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/new-account" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-bank-card-line mr-3" }),
                                            "Rekening Baru"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/account-list",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/account-list" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-bank-line mr-3" }),
                                            "Daftar Rekening"
                                        ])
                                    ])
                                ])
                            ])
                        ] : null,

                        // Show Anggota/Nasabah menu to Kasir
                        // Show Simpanan menu to Kasir only (Setoran, Penarikan)
                        isKasir ? [
                            m("li", [
                                m("a", {
                                    href: "#",
                                    class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        this.isSimpananOpen = !this.isSimpananOpen;
                                    }
                                }, [
                                    m("div", { class: "flex items-center" }, [
                                        m("i", { class: "ri-bank-line mr-3 text-lg" }),
                                        this.isSidebarOpen && m("span", { class: "font-medium truncate" }, "Simpanan"),
                                        (this.isSidebarOpen && this.missedJasaCount > 0) && m("span", {
                                            class: "ml-2 flex h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-800"
                                        })
                                    ]),
                                    this.isSidebarOpen && m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isSimpananOpen ? "transform rotate-180" : "") })
                                ]),
                                // Collapsible Menu for Kasir Simpanan
                                this.isSimpananOpen && this.isSidebarOpen && m("ul", { class: "mt-1 space-y-1 pl-4 max-w-full" }, [
                                    m("li", [
                                        m("a", {
                                            href: "/savings-deposits",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/savings-deposits" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-add-circle-line mr-3" }),
                                            "Setoran"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/savings-withdrawals",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/savings-withdrawals" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-money-dollar-circle-line mr-3" }),
                                            "Penarikan"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/savings-interest",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/savings-interest" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("div", { class: "flex items-center justify-between w-full" }, [
                                                m("div", { class: "flex items-center" }, [
                                                    m("i", { class: "ri-hand-coin-line mr-3" }),
                                                    "Pembayaran Jasa"
                                                ]),
                                                this.missedJasaCount > 0 && m("span", {
                                                    class: "flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full"
                                                }, this.missedJasaCount)
                                            ])
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/savings-performance",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/savings-performance" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-line-chart-line mr-3" }),
                                            "Kinerja Simpanan"
                                        ])
                                    ])
                                ])
                            ])
                        ] : null,

                        // Show Simpanan menu to Kasir only (Setoran, Penarikan)
                        // Show Pinjaman menu to Kasir only (Angsuran, Penyelesaian)
                        isKasir ? [
                            m("li", [
                                m("a", {
                                    href: "#",
                                    class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        this.isPinjamanOpen = !this.isPinjamanOpen;
                                    }
                                }, [
                                    m("div", { class: "flex items-center" }, [
                                        m("i", { class: "ri-file-text-line mr-3 text-lg" }),
                                        this.isSidebarOpen && m("span", { class: "font-medium truncate" }, "Pinjaman")
                                    ]),
                                    this.isSidebarOpen && m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isPinjamanOpen ? "transform rotate-180" : "") })
                                ]),
                                // Collapsible Menu for Kasir Pinjaman
                                this.isPinjamanOpen && this.isSidebarOpen && m("ul", { class: "mt-1 space-y-1 pl-4 max-w-full" }, [
                                    m("li", [
                                        m("a", {
                                            href: "/loan-installments",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/loan-installments" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-list-check mr-3" }),
                                            "Angsuran"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/loan-settlements",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/loan-settlements" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-flag-2-line mr-3" }),
                                            "Penyelesaian"
                                        ])
                                    ])
                                ])
                            ])
                        ] : null,

                        // Akuntansi Menu for Subunit Staff (Managers, Akuntansi, and Kasir)
                        (isSubunitStaff) ? [
                            m("li", [
                                m("a", {
                                    href: "#",
                                    class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        this.isAkuntansiOpen = !this.isAkuntansiOpen;
                                    }
                                }, [
                                    m("div", { class: "flex items-center" }, [
                                        m("i", { class: "ri-book-2-line mr-3 text-lg" }),
                                        this.isSidebarOpen && m("span", { class: "font-medium truncate" }, "Akuntansi")
                                    ]),
                                    this.isSidebarOpen && m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isAkuntansiOpen ? "transform rotate-180" : "") })
                                ]),
                                this.isAkuntansiOpen && this.isSidebarOpen && m("ul", { class: "mt-1 space-y-1 pl-4 max-w-full" }, [
                                    m("li", [
                                        m("a", {
                                            href: "/accounting/posting",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/accounting/posting" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-checkbox-multiple-line mr-3" }),
                                            "Posting Jurnal"
                                        ])
                                    ]),
                                    !isKasir && m("li", [
                                        m("a", {
                                            href: "/accounting/general-journal",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/accounting/general-journal" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-booklet-line mr-3" }),
                                            "Jurnal Umum"
                                        ])
                                    ]),
                                    // Laporan Submenu
                                    !isKasir && m("li", [
                                        m("a", {
                                            href: "#",
                                            class: "flex items-center justify-between w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md",
                                            onclick: (e) => {
                                                e.preventDefault();
                                                this.isLaporanOpen = !this.isLaporanOpen;
                                            }
                                        }, [
                                            m("div", { class: "flex items-center" }, [
                                                m("i", { class: "ri-file-chart-line mr-3" }),
                                                "Laporan"
                                            ]),
                                            m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isLaporanOpen ? "transform rotate-180" : "") })
                                        ]),
                                        this.isLaporanOpen && m("ul", { class: "mt-1 space-y-1 pl-4 border-l border-gray-200 dark:border-gray-700 ml-4" }, [
                                            m("li", [
                                                m("a", {
                                                    href: "/accounting/reports/balance-sheet",
                                                    class: "flex items-center px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-gray-800 rounded-md " +
                                                        (m.route.get() === "/accounting/reports/balance-sheet" ? "text-orange-600 dark:text-orange-400 font-medium bg-orange-50 dark:bg-gray-800" : ""),
                                                    oncreate: m.route.link
                                                }, "Posisi Keuangan (Neraca)")
                                            ]),
                                            m("li", [
                                                m("a", {
                                                    href: "/accounting/reports/income-statement",
                                                    class: "flex items-center px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-gray-800 rounded-md " +
                                                        (m.route.get() === "/accounting/reports/income-statement" ? "text-orange-600 dark:text-orange-400 font-medium bg-orange-50 dark:bg-gray-800" : ""),
                                                    oncreate: m.route.link
                                                }, "Perhitungan Hasil Usaha")
                                            ]),
                                            isSubunitManager && m("li", [
                                                m("a", {
                                                    href: "/accounting/reports/phu-allocation",
                                                    class: "flex items-center px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-gray-800 rounded-md " +
                                                        (m.route.get() === "/accounting/reports/phu-allocation" ? "text-orange-600 dark:text-orange-400 font-medium bg-orange-50 dark:bg-gray-800" : ""),
                                                    oncreate: m.route.link
                                                }, "Perhitungan PHU")
                                            ]),
                                            m("li", [
                                                m("a", {
                                                    href: "/accounting/reports/equity-change",
                                                    class: "flex items-center px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-gray-800 rounded-md " +
                                                        (m.route.get() === "/accounting/reports/equity-change" ? "text-orange-600 dark:text-orange-400 font-medium bg-orange-50 dark:bg-gray-800" : ""),
                                                    oncreate: m.route.link
                                                }, "Perubahan Ekuitas")
                                            ]),
                                            m("li", [
                                                m("a", {
                                                    href: "/accounting/reports/cash-flow",
                                                    class: "flex items-center px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-gray-800 rounded-md " +
                                                        (m.route.get() === "/accounting/reports/cash-flow" ? "text-orange-600 dark:text-orange-400 font-medium bg-orange-50 dark:bg-gray-800" : ""),
                                                    oncreate: m.route.link
                                                }, "Arus Kas")
                                            ]),
                                            m("li", [
                                                m("a", {
                                                    href: "/accounting/reports/calk",
                                                    class: "flex items-center px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-gray-800 rounded-md " +
                                                        (m.route.get() === "/accounting/reports/calk" ? "text-orange-600 dark:text-orange-400 font-medium bg-orange-50 dark:bg-gray-800" : ""),
                                                    oncreate: m.route.link
                                                }, "CALK")
                                            ]),
                                        ])
                                    ]),
                                ])
                            ])
                        ] : null,

                        // Aktivitas Lain Menu for Subunit Staff (Managers, Akuntansi, and Kasir)
                        (isSubunitStaff) ? [
                            m("li", [
                                m("a", {
                                    href: "#",
                                    class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        this.isAktivitasLainOpen = !this.isAktivitasLainOpen;
                                    }
                                }, [
                                    m("div", { class: "flex items-center" }, [
                                        m("i", { class: "ri-file-list-2-line mr-3 text-lg" }),
                                        this.isSidebarOpen && m("span", { class: "font-medium truncate" }, "Aktivitas Lain")
                                    ]),
                                    this.isSidebarOpen && m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isAktivitasLainOpen ? "transform rotate-180" : "") })
                                ]),
                                this.isAktivitasLainOpen && this.isSidebarOpen && m("ul", { class: "mt-1 space-y-1 pl-4 max-w-full" }, [
                                    m("li", [
                                        m("a", {
                                            href: "/belanja",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/belanja" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-hand-coin-line mr-3" }),
                                            "Belanja"
                                        ])
                                    ])
                                ])
                            ])
                        ] : null,

                        // Kinerja Menu for Subunit Managers
                        isSubunitManager ? [
                            m("li", [
                                m("a", {
                                    href: "#",
                                    class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        this.isKinerjaOpen = !this.isKinerjaOpen;
                                    }
                                }, [
                                    m("div", { class: "flex items-center" }, [
                                        m("i", { class: "ri-bar-chart-box-line mr-3 text-lg" }),
                                        this.isSidebarOpen && m("span", { class: "font-medium truncate" }, "Kinerja")
                                    ]),
                                    this.isSidebarOpen && m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isKinerjaOpen ? "transform rotate-180" : "") })
                                ]),
                                // Collapsible Menu for Kinerja
                                this.isKinerjaOpen && this.isSidebarOpen && m("ul", { class: "mt-1 space-y-1 pl-4 max-w-full" }, [
                                    m("li", [
                                        m("a", {
                                            href: "/kinerja/target",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/kinerja/target" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-focus-3-line mr-3" }),
                                            "Target"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/kinerja/realisasi",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md " +
                                                (m.route.get() === "/kinerja/realisasi" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-line-chart-line mr-3" }),
                                            "Realisasi"
                                        ])
                                    ])
                                ])
                            ])
                        ] : null,

                        // Show Koordinator link only to managers (NOT superusers) OR Kasir
                        (((isManager || isAdmin) && !isSuperuser) || isKasir) ? [
                            m("li", [
                                m("a", {
                                    href: "#",
                                    class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        // Toggle koordinator menu
                                        this.isKoordinatorOpen = !this.isKoordinatorOpen;
                                    }
                                }, [
                                    m("div", { class: "flex items-center" }, [
                                        m("i", { class: "ri-user-star-line mr-3 text-lg" }),
                                        this.isSidebarOpen && m("span", { class: "font-medium truncate" }, "Petugas")
                                    ]),
                                    this.isSidebarOpen && m("i", { class: "ri-arrow-down-s-line text-sm " + (this.isKoordinatorOpen ? "transform rotate-180" : "") })
                                ]),
                                // Collapsible Menu for Koordinator
                                this.isKoordinatorOpen && this.isSidebarOpen && m("ul", { class: "mt-1 space-y-1 pl-4 max-w-full" }, [
                                    m("li", [
                                        m("a", {
                                            href: "/petugas-aktivitas",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                                (m.route.get() === "/petugas-aktivitas" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-run-fill mr-3" }),
                                            "Aktivitas"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/petugas-setoran",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                                (m.route.get() === "/petugas-setoran" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-bank-line mr-3" }),
                                            "Setoran"
                                        ])
                                    ]),
                                    m("li", [
                                        m("a", {
                                            href: "/petugas-fund-management",
                                            class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                                (m.route.get() === "/petugas-fund-management" ? "bg-orange-100 dark:bg-orange-900/50 text-gray-900 dark:text-white" : ""),
                                            oncreate: m.route.link
                                        }, [
                                            m("i", { class: "ri-money-dollar-circle-line mr-3" }),
                                            "Penggantian/Penitipan Dana"
                                        ])
                                    ])
                                ])
                            ])
                        ] : null,
                        // Only show these menu items to superusers
                        isSuperuser && !isKoordinator ? [
                            m("li", [
                                m("a", {
                                    href: "/badanusaha",
                                    class: "flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors " +
                                        (m.route.get() === "/badanusaha" ? "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-orange-500 dark:border-orange-400 text-gray-900 dark:text-white" : ""),
                                    oncreate: m.route.link
                                }, [
                                    m("i", { class: "ri-community-line mr-3 text-lg" }),
                                    this.isSidebarOpen && m("span", { class: "font-medium truncate" }, "Badan dan Unit Usaha")
                                ])
                            ]),
                            m("li", [
                                m("a", {
                                    href: "/manajer",
                                    class: "flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors " +
                                        (m.route.get() === "/manajer" ? "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-orange-500 dark:border-orange-400 text-gray-900 dark:text-white" : ""),
                                    oncreate: m.route.link
                                }, [
                                    m("i", { class: "ri-group-line mr-3 text-lg" }),
                                    this.isSidebarOpen && m("span", { class: "font-medium" }, "Manajer")
                                ])
                            ]),
                            m("li", [
                                m("a", {
                                    href: "/coareference",
                                    class: "flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors " +
                                        (m.route.get() === "/coareference" ? "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-orange-500 dark:border-orange-400 text-gray-900 dark:text-white" : ""),
                                    oncreate: m.route.link
                                }, [
                                    m("i", { class: "ri-book-2-line mr-3 text-lg" }),
                                    this.isSidebarOpen && m("span", { class: "font-medium truncate" }, "Referensi Bagan Akun")
                                ])
                            ]),
                            m("li", [
                                m("a", {
                                    href: "/backup",
                                    class: "flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750 rounded-lg mx-2 transition-colors " +
                                        (m.route.get() === "/backup" ? "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-orange-500 dark:border-orange-400 text-gray-900 dark:text-white" : ""),
                                    oncreate: m.route.link
                                }, [
                                    m("i", { class: "ri-database-2-line mr-3 text-lg" }),
                                    this.isSidebarOpen && m("span", { class: "font-medium truncate" }, "Backup Database")
                                ])
                            ])
                        ] : null,
                    ])
                ]),

                //    Sidebar footer
                m("div", { class: "p-4 border-t border-gray-200 dark:border-gray-700" }, [
                    m("div", { class: "mb-2" }, [
                        // Dev Tools section removed
                    ]),
                    // Add Pengaturan section for managers (not superusers)
                    ((isManager || isAdmin) && !isSuperuser) ? [
                        m("div", { class: "mb-2" }, [
                            m("a", {
                                href: "#",
                                class: "flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-gray-750",
                                onclick: (e) => {
                                    e.preventDefault();
                                    // Toggle pengaturan menu
                                    this.isPengaturanOpen = !this.isPengaturanOpen;
                                }
                            }, [
                                m("div", { class: "flex items-center" }, [
                                    m("i", { class: "ri-settings-line mr-3 text-lg" }),
                                    this.isSidebarOpen && m("span", "Pengaturan"),
                                    // Notification Badge for incomplete preferences
                                    (this.isSidebarOpen && userData.preferencesComplete === false) && m("span", {
                                        class: "ml-2 flex h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-800"
                                    })
                                ]),
                                this.isSidebarOpen && m("i", { class: this.isPengaturanOpen ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line" })
                            ]),
                            // Collapsible Menu for Pengaturan
                            this.isPengaturanOpen && this.isSidebarOpen && m("ul", { class: "pl-8 py-1 space-y-1 max-w-full" }, [
                                m("li", [
                                    m("a", {
                                        href: "/umum",
                                        class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                            (m.route.get() === "/umum" ? "bg-orange-200 dark:bg-orange-900 text-gray-900 dark:text-white" : ""),
                                        oncreate: m.route.link
                                    }, [
                                        m("i", { class: "ri-building-line mr-3" }),
                                        m("div", { class: "flex items-center justify-between w-full" }, [
                                            "Umum",
                                            userData.preferencesComplete === false && m("span", {
                                                class: "flex h-1.5 w-1.5 rounded-full bg-red-500"
                                            })
                                        ])
                                    ])
                                ]),
                                m("li", [
                                    m("a", {
                                        href: "/officers",
                                        class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                            (m.route.get() === "/officers" ? "bg-orange-200 dark:bg-orange-900 text-gray-900 dark:text-white" : ""),
                                        oncreate: m.route.link
                                    }, [
                                        m("i", { class: "ri-user-settings-line mr-3" }),
                                        "Pejabat"
                                    ])
                                ]),
                                m("li", [
                                    m("a", {
                                        href: "/produk",
                                        class: "flex items-center px-4 py-2 text-sm text-gray-600 dark-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                            (m.route.get() === "/produk" ? "bg-orange-200 dark:bg-orange-900 text-gray-900 dark:text-white" : ""),
                                        oncreate: m.route.link
                                    }, [
                                        m("i", { class: "ri-box-3-line mr-3" }),
                                        "Produk"
                                    ])
                                ]),
                                m("li", [
                                    m("a", {
                                        href: "/coareference",
                                        class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                            (m.route.get() === "/coareference" ? "bg-orange-200 dark:bg-orange-900 text-gray-900 dark:text-white" : ""),
                                        oncreate: m.route.link
                                    }, [
                                        m("i", { class: "ri-book-2-line mr-3" }),
                                        "Bagan Akun"
                                    ])
                                ]),
                                m("li", [
                                    m("a", {
                                        href: "/nomenclature-settings",
                                        class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                            (m.route.get() === "/nomenclature-settings" ? "bg-orange-200 dark:bg-orange-900 text-gray-900 dark:text-white" : ""),
                                        oncreate: m.route.link
                                    }, [
                                        m("i", { class: "ri-global-line mr-3" }),
                                        "Terminologi"
                                    ])
                                ]),
                                m("li", [
                                    m("a", {
                                        href: "/subunit-backup",
                                        class: "flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-orange-100 dark:hover:bg-gray-650 rounded-md truncate w-full " +
                                            (m.route.get() === "/subunit-backup" ? "bg-orange-200 dark:bg-orange-900 text-gray-900 dark:text-white" : ""),
                                        oncreate: m.route.link
                                    }, [
                                        m("i", { class: "ri-archive-line mr-3" }),
                                        "Backup Data"
                                    ])
                                ])
                            ])
                        ])
                    ] : null,
                    m("a", {
                        href: "#",
                        class: "flex items-center text-red-500 hover:text-red-700 dark:hover:text-red-400",
                        onclick: (e) => {
                            e.preventDefault();
                            this.handleLogout();
                        }
                    }, [
                        m("i", { class: "ri-logout-box-line mr-3 text-lg" }),
                        this.isSidebarOpen && m("span", "Logout")
                    ])
                ])
            ]), // End of sidebar

            // Main content area
            m("div", { class: "flex-1 flex flex-col overflow-hidden print:block print:overflow-visible print:h-auto" }, [
                // Navbar - simplified for unprotected route when not authenticated, otherwise show full navbar
                m("header", { class: "bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 print:hidden h-20 min-h-[5rem]" }, [
                    m("div", { class: "flex items-center justify-between px-4 h-full" }, [
                        m("h2", { class: "text-lg font-semibold text-gray-800 dark:text-white" }, getTerm(vnode.attrs.title || "Vitta Simpan Pinjam", (vnode.attrs.title || "Vitta Simpan Pinjam").split('.').pop())),
                        m("div", { class: "flex items-center" }, [
                            // Show login button for unprotected route when not authenticated, otherwise show full navbar
                            (isUnprotectedRoute && !isAuthenticated) ?
                                m("a", {
                                    href: "/login",
                                    class: "px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500",
                                    oncreate: m.route.link
                                }, "Login") :
                                [
                                    // Theme toggle button
                                    m("button", {
                                        class: "p-2 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none mr-2 cursor-pointer",
                                        onclick: () => this.toggleTheme()
                                    }, [
                                        m("i", { class: this.isDarkMode ? "ri-sun-line text-xl" : "ri-contrast-2-line text-xl" })
                                    ]),
                                    // User profile section
                                    m("div", { class: "relative" }, [
                                        m("button", {
                                            class: "w-10 h-10 rounded-full " + this.userAvatarColor + " flex items-center justify-center text-white cursor-pointer relative",
                                            onclick: () => this.toggleDropdown()
                                        }, [
                                            avatarLetter,
                                            // Red dot for MFA warning (show if NOT enabled, including undefined)
                                            (!mfaEnabled) ? m("span", {
                                                class: "absolute top-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-white bg-red-500 transform translate-x-1/4 -translate-y-1/4"
                                            }) : null
                                        ]),

                                        // Dropdown menu
                                        this.isDropdownOpen ? m("div", {
                                            class: "origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                                        }, [
                                            m("div", { class: "py-1" }, [
                                                m("div", { class: "px-4 py-2 border-b border-gray-200 dark:border-gray-700" }, [
                                                    m("p", { class: "text-sm font-medium text-gray-800 dark:text-white" }, fullName),
                                                    m("p", { class: "text-xs text-gray-500 dark:text-gray-400 truncate" }, email || username),
                                                    m("p", { class: "text-xs text-gray-500 dark:text-gray-400" }, role)
                                                ]),
                                                m("a", {
                                                    href: "#",
                                                    class: "block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between",
                                                    onclick: (e) => {
                                                        e.preventDefault();
                                                        this.goToProfile();
                                                    }
                                                }, [
                                                    m("div", { class: "flex items-center" }, [
                                                        m("i", { class: "ri-user-line mr-2" }),
                                                        "Profil"
                                                    ]),
                                                    // MFA Warning Badge
                                                    (!mfaEnabled) ? m("span", {
                                                        class: "inline-flex items-center justify-center px-2 py-0.5 ml-2 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full",
                                                        title: "Aktifkan Autentikasi 2 Faktor untuk keamanan akun Anda"
                                                    }, "!") : null
                                                ]),
                                                m("a", {
                                                    href: "#",
                                                    class: "block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                                                    onclick: (e) => {
                                                        e.preventDefault();
                                                        this.toggleTheme();
                                                    }
                                                }, [
                                                    m("i", { class: "ri-contrast-2-line mr-2" }),
                                                    this.isDarkMode ? "Mode Terang" : "Mode Gelap"
                                                ]),
                                                m("a", {
                                                    href: "/login",
                                                    class: "block px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700",
                                                    onclick: () => this.handleLogout()
                                                }, [
                                                    m("i", { class: "ri-logout-box-line mr-2" }),
                                                    "Keluar"
                                                ])
                                            ])
                                        ]) : null
                                    ])
                                ]
                        ])
                    ])
                ]),

                // Main content
                userData.passwordAgeOverdue && m("div", { class: "bg-amber-100 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 py-3 px-6 flex items-center justify-between animate-pulse-slow" }, [
                    m("div", { class: "flex items-center text-amber-800 dark:text-amber-200 text-sm font-medium" }, [
                        m("i", { class: "ri-error-warning-line mr-2 text-xl" }),
                        m("span", "Password Anda sudah lebih dari 30 hari. Silakan ganti password segera untuk menjaga keamanan akun.")
                    ]),
                    m("a", {
                        href: "/profile",
                        oncreate: m.route.link,
                        class: "ml-4 bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap"
                    }, "Ganti Sekarang")
                ]),
                m("main", { class: "flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900 print:p-0 print:bg-white print:overflow-visible" }, [
                    vnode.attrs.content ? m(vnode.attrs.content) : vnode.children
                ]),

                // Footer
                m("footer", { class: "bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-4 px-6 print:hidden" }, [
                    m("div", { class: "text-sm text-center space-y-2" }, [
                        m("p", { class: "text-gray-600 dark:text-gray-400" }, "© 2025 Vitta Simpan Pinjam. Semua hak dilindungi."),
                        // Add conditional additional text
                        (function () {
                            const subunit_name = userData.subunit_name || '';
                            if (role === 'superuser') {
                                return m("p", { class: "text-gray-500 dark:text-gray-500" }, [
                                    "Teknologi disediakan oleh ",
                                    m("a", {
                                        href: "https://www.solusiti.com",
                                        target: "_blank",
                                        rel: "noopener noreferrer",
                                        class: "text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-medium"
                                    }, "PT Solusi Teknologi Informasi")
                                ]);
                            } else if (role === 'manajer' && subunit_name) {
                                return m("p", { class: "font-mono font-semibold text-gray-500 dark:text-gray-500" }, "Penggunaan Aplikasi diberikan kepada " + subunit_name);
                            } else {
                                return null;
                            }
                        })(),
                        // Links
                        m("div", { class: "flex justify-center space-x-4" }, [
                            m("a", {
                                href: "#",
                                class: "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 text-sm"
                            }, "Syarat"),
                            m("a", {
                                href: "#",
                                class: "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 text-sm"
                            }, "Privasi"),
                            m("a", {
                                href: "https://www.solusiti.com",
                                target: "_blank",
                                rel: "noopener noreferrer",
                                class: "text-gray-500 dark:text-gray-400 hover:text-gray-300 text-sm"
                            }, "Kontak")
                        ])
                    ])
                ])
            ])
        ]);
    }
};

export default Layout;
